import React, {useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { realText } from '../../utils/references';
import { policyholderPrefill } from './convertPrefill';
import { S, Icon, BtnPrimary, BtnSecondary } from '../regureStyles';
import { useTenantPolicyLobFamilies } from '../../utils/productCatalog';
import { useSelector } from 'react-redux';
import { policyPeriodFromOffer } from '../../utils/contractDates';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Label keys for every Policy.lineOfBusiness family this form can offer.
// Which of them a tenant actually SEES is derived from its configured
// products (see useTenantPolicyLobFamilies) — a tenant selling only
// RC Pro / PGMA / PGM has no business being offered Legal Protection or
// Surplus Lines here. Unconfigured tenants see all five, unchanged.
const LOB_LABEL_KEYS = {
  professional_liability: 'screens.policiesScreen.newPanel.lobProfessionalLiabilityEO',
  daily_sickness:         'screens.policiesScreen.newPanel.lobDailySicknessIJM',
  legal_protection:       'screens.policiesScreen.newPanel.lobLegalProtectionPJ',
  errors_omissions:       'screens.policiesScreen.newPanel.lobErrorsOmissions',
  surplus_lines:          'screens.policiesScreen.newPanel.lobSurplusLines',
};

// Carriers list response shapes vary; normalise to an array.
function extractCarriers(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  return [];
}

// ─── Field Component (must be outside component to avoid unmount/remount on every render) ─────
const FormField = ({ label, children, labelStyle }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// Same product-family mapping as backend/controllers/policyController.js.
// Kept in sync so the UI shows the correct LOB the moment the offer is
// selected — the backend also merges from the offer as a defence in depth.
const OFFER_LOB_TO_POLICY_LOB = {
  PGMA:   'daily_sickness',
  PGM:    'daily_sickness',
  IJM:    'daily_sickness',
  RC_PRO: 'professional_liability',
  'E&O':  'errors_omissions',
  PJ:     'legal_protection',
};

// Products whose quote does NOT ask the user for a risk carrier.
//
// This panel is two screens wearing one component: the standalone "New Policy"
// form, and the panel that opens behind "Convert to Policy" on a quote. The
// Risk Carrier dropdown was added here for the standalone form (a2a5adeb, to
// give bordereaux validation a riskCarrier FK to read), and then arrived on the
// quote path for free when Convert to Policy reused the component (051de8c7) —
// which prefills the LOB, so the field renders the instant the panel opens.
// Nobody asked for it there. For these products the carrier is a fact of the
// product, not a question for the person writing the quote.
const QUOTE_PRODUCTS_WITHOUT_CARRIER_FIELD = ['RC_PRO', 'PGMA', 'PGM'];

const NewPolicyPanel = React.memo(function NewPolicyPanel({ onClose, onCreated, sourceOffer }) {
  // The tenant's configured base currency — the same TenantConfig.locale value
  // the money formatter resolves against, so the form's label and the stored
  // value agree. Falls back to nothing rather than to a market-specific guess.
  const tenantCurrency = useSelector(state => state?.tenantConfig?.config?.locale?.currency) || '';
  const { t } = useTranslation();
  // Lines of business this tenant sells, derived from TenantConfig.products.
  const lobFamilies = useTenantPolicyLobFamilies();
  // Pre-fill from the source offer when converting. Empty defaults on
  // fresh "New Policy" (sourceOffer undefined).
  const initialLob = sourceOffer?.lineOfBusiness
    ? (OFFER_LOB_TO_POLICY_LOB[sourceOffer.lineOfBusiness] || sourceOffer.lineOfBusiness)
    : '';
  const initialCanton = sourceOffer?.params?.policyholder?.canton || '';
  // The policy period comes from the offer, in this order: the PERSISTED
  // coverage the client was shown on the document, else the offer's
  // inception with the end resolved from the tenant's contract-expiry
  // convention. Both stay editable so the user can push the period out if
  // the offer sat unaccepted.
  //
  // This used to be `inception + 1 year`, computed here and submitted, so
  // ARGOS policies ended on the inception anniversary instead of the
  // 31 December every AROMED contract runs to — and because the panel sent
  // the value, the backend's own resolution never ran. The offer's
  // `expiresAt` (quote validity) is deliberately not consulted: an
  // acceptance deadline is not a coverage date.
  const tenantConfig = useSelector(state => state?.tenantConfig?.config) || null;
  const initialPeriod = policyPeriodFromOffer(sourceOffer, tenantConfig);
  const initialStart = initialPeriod.start;
  const initialEnd = initialPeriod.end;
  // Never a legal form in either field — see convertPrefill.js.
  const prefill = sourceOffer ? policyholderPrefill(sourceOffer) : { name: '', company: '' };
  const [name, setName] = useState(prefill.name);
  const [company, setCompany] = useState(prefill.company);
  const [lob, setLob] = useState(initialLob);
  const [premium, setPremium] = useState(sourceOffer?.premiumCalc ? String(sourceOffer.premiumCalc) : '');
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [canton, setCanton] = useState(initialCanton);
  // `broker` holds the selected Broker's ObjectId (empty when none picked).
  // Prior version was a free-text name; that meant Policy.broker was never
  // populated as an ObjectId, so the commission auto-gen hook always failed
  // with `no_broker` (Test Run 7-23 A4.6). We now pick from the tenant's
  // Broker collection and send both `broker` (ObjectId) and `brokerName`
  // (denormalised label) on create.
  const [broker, setBroker] = useState(sourceOffer?.brokerId ? String(sourceOffer.brokerId) : '');
  const [brokerList, setBrokerList] = useState([]);
  const [state, setState] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [carrierType, setCarrierType] = useState('non_admitted');
  const [riskCarrier, setRiskCarrier] = useState('');
  const [carrierList, setCarrierList] = useState([]);
  // Converting an RC Pro / PGMA / PGM quote: no carrier question, so no
  // carrier state to collect and no reason to go and fetch the list.
  const hideCarrierField = !!sourceOffer
    && QUOTE_PRODUCTS_WITHOUT_CARRIER_FIELD.includes(sourceOffer.lineOfBusiness);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hideCarrierField) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/risk-carriers`, { withCredentials: true });
        if (!cancelled) setCarrierList(extractCarriers(resp.data));
      } catch {
        // Non-fatal — the field simply shows an empty list and stays optional.
      }
    })();
    return () => { cancelled = true; };
  }, [hideCarrierField]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/brokers?limit=500&status=active`, { withCredentials: true });
        if (!cancelled) setBrokerList(Array.isArray(resp.data?.data) ? resp.data.data : []);
      } catch {
        // Non-fatal — dropdown just shows empty; broker stays optional.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Restrict the carrier dropdown to those whose `products` cover the
  // selected line of business. Carriers with no `products` field match any.
  // Normalize labels ('Daily Sickness') and codes ('daily_sickness') so older
  // seeds that stored human labels still match new-style LOB enum values.
  const normalizeProduct = (p) => String(p || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  const eligibleCarriers = lob && lob !== 'surplus_lines' && !hideCarrierField
    ? carrierList.filter(c => !Array.isArray(c.products) || c.products.length === 0 || c.products.map(normalizeProduct).includes(lob))
    : [];

  const handleCreate = async () => {
    if (!realText(name)) {  // a bare "—" is not a name
      setError(t('screens.policiesScreen.newPanel.policyholderRequiredError'));
      return;
    }
    if (!lob) {
      setError(t('screens.policiesScreen.newPanel.lobRequiredError'));
      return;
    }
    if (!premium || Number(premium) <= 0) {
      setError(t('screens.policiesScreen.newPanel.premiumRequiredError'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const selectedBroker = broker ? brokerList.find(b => String(b._id) === String(broker)) : null;
      const body = {
        policyholder: { name: name.trim(), ...(company.trim() && { company: company.trim() }) },
        lineOfBusiness: lob,
        annualPremium: Number(premium),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(canton.trim() && { canton: canton.trim() }),
        // Send both the Broker ObjectId (drives commission auto-gen) and
        // the denormalised label so downstream views that read brokerName
        // don't need to populate.
        ...(selectedBroker && { broker: selectedBroker._id, brokerName: selectedBroker.name }),
        // Bind back to the source offer so the backend can (a) block a
        // second conversion via Offer.convertedTo, (b) log the conversion,
        // and (c) fill any remaining blanks (product, currency, clientId,
        // coverageDetails) from the offer as a defence in depth.
        ...(sourceOffer?._id && { offer: sourceOffer._id }),
        ...(sourceOffer?.currency && { currency: sourceOffer.currency }),
        status: 'active',
      };
      if (lob === 'surplus_lines') {
        if (state) body.state = state;
        if (carrierName.trim()) body.carrierName = carrierName.trim();
        if (carrierType) body.carrierType = carrierType;
        body.currency = 'USD';
      } else if (riskCarrier) {
        // Link to the selected risk carrier and snapshot its name so the
        // policy passes bordereaux validation even before the populate fires.
        const selected = carrierList.find(c => String(c._id) === String(riskCarrier));
        body.riskCarrier = riskCarrier;
        if (selected?.name) body.carrierName = selected.name;
      }
      const resp = await axios.post(`${BASE_URL}/policies`, body, { withCredentials: true });
      // Surface auto-commission gen result so the tester can immediately
      // see whether a commission record was created (Test Run 7-22 A3.10).
      // Non-blocking — logs and toasts, doesn't fail the create.
      const cag = resp?.data?.commissionAutoGen;
      if (cag) {
        try {
          const { default: toast } = await import('react-hot-toast');
          if (cag.recordCount > 0) {
            toast.success(`${cag.recordCount} commission record(s) auto-created for ${cag.brokerName || 'broker'}.`);
          } else if (cag.reason) {
            const productName = cag.product || 'this product';
            const label = {
              no_broker:           'no broker set on this policy',
              no_product_or_offer: 'no product or offer set (add one to auto-generate)',
              no_default_plan:     'no active default commission plan for this tenant',
              broker_not_found:    'broker id did not resolve to a broker record',
              no_product:          'no product could be resolved from the policy or its offer',
              no_rate:             `no commission rate configured for ${productName} on the active plan`,
              no_basis:            'no premium basis on the policy (annualPremium/netPremium is 0 or missing)',
              error:               `error: ${cag.error || 'unknown'}`,
            }[cag.reason] || cag.reason;
            toast(`Policy created. Commission not generated — ${label}.`, { icon: '⚠️', duration: 5000 });
          }
        } catch (_) { /* toast library unavailable — skip */ }
      }
      onCreated && onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('screens.policiesScreen.newPanel.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 13px',
    background: '#fff',
    border: `1px solid ${S.borderLight}`,
    borderRadius: 10,
    fontSize: 13,
    color: S.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: S.textSecondary, display: 'block', marginBottom: 5 };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.3)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{ width: 480, height: '100%', background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)', overflowY: 'auto', padding: 28 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: sourceOffer ? 12 : 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>
            {sourceOffer
              ? t('screens.policiesScreen.newPanel.convertTitle', { defaultValue: 'Convert Offer to Policy' })
              : t('screens.policiesScreen.newPanel.title')}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        {sourceOffer && (
          <div style={{ marginBottom: 20, padding: '10px 14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
            {t('screens.policiesScreen.newPanel.convertBanner', {
              defaultValue: 'Pre-filled from offer {{offerNumber}}. Review and adjust before binding — set effective dates below.',
              offerNumber: sourceOffer.offerNumber || sourceOffer.id || '',
            })}
          </div>
        )}

        <FormField label={t('screens.policiesScreen.newPanel.policyholderNameRequired')} labelStyle={labelStyle}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder={t('screens.policiesScreen.newPanel.policyholderNamePlaceholder')} style={inputStyle} />
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.companyPractice')}>
          <input value={company} onChange={e => setCompany(e.target.value)} placeholder={t('screens.policiesScreen.newPanel.companyPlaceholder')} style={inputStyle} />
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.lineOfBusinessRequired')}>
          <select value={lob} onChange={e => setLob(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value=''>{t('screens.policiesScreen.newPanel.selectProduct')}</option>
            {lobFamilies.map(value => (
              <option key={value} value={value}>
                {LOB_LABEL_KEYS[value] ? t(LOB_LABEL_KEYS[value]) : value}
              </option>
            ))}
          </select>
        </FormField>
        {lob === 'surplus_lines' && (
          <>
            <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.stateRequired')}>
              <select value={state} onChange={e => setState(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value=''>{t('screens.policiesScreen.newPanel.selectState')}</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.carrierName')}>
              <input value={carrierName} onChange={e => setCarrierName(e.target.value)} placeholder={t('screens.policiesScreen.newPanel.carrierNamePlaceholder')} style={inputStyle} />
            </FormField>
            <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.carrierType')}>
              <select value={carrierType} onChange={e => setCarrierType(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value='non_admitted'>{t('screens.policiesScreen.newPanel.carrierNonAdmitted')}</option>
                <option value='admitted'>{t('screens.policiesScreen.newPanel.carrierAdmitted')}</option>
              </select>
            </FormField>
          </>
        )}
        {lob && lob !== 'surplus_lines' && !hideCarrierField && (
          <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.riskCarrier')}>
            <select value={riskCarrier} onChange={e => setRiskCarrier(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value=''>{eligibleCarriers.length ? t('screens.policiesScreen.newPanel.selectCarrier') : t('screens.policiesScreen.newPanel.noCarriersConfigured')}</option>
              {eligibleCarriers.map(c => (
                <option key={c._id} value={c._id}>
                  {c.name}{c.code ? ` (${c.code})` : ''}
                </option>
              ))}
            </select>
          </FormField>
        )}
        {/* The currency in this label was hardcoded: USD for surplus lines,
            CHF for everything else. On a EUR tenant it asked for an annual
            premium in Swiss francs and the value was then stored and rendered
            as euros — a label contradicting the number underneath it.
            The currency shown is the tenant's own, resolved the same way every
            money value on the screen is. Surplus lines is a US line written in
            USD by definition, so it keeps its explicit currency. */}
        <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.annualPremiumRequired', { currency: lob === 'surplus_lines' ? 'USD' : tenantCurrency })}>
          <input type='number' value={premium} onChange={e => setPremium(e.target.value)} placeholder={t('screens.policiesScreen.newPanel.annualPremiumPlaceholder')} style={inputStyle} />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.startDate')}>
            <input type='date' value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
          </FormField>
          <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.endDate')}>
            <input type='date' value={endDate} onChange={e => setEndDate(e.target.value)} style={inputStyle} />
          </FormField>
        </div>
        <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.canton')}>
          <input value={canton} onChange={e => setCanton(e.target.value)} placeholder={t('screens.policiesScreen.newPanel.cantonPlaceholder')} style={inputStyle} />
        </FormField>
        <FormField labelStyle={labelStyle} label={t('screens.policiesScreen.newPanel.brokerName')}>
          <select value={broker} onChange={e => setBroker(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value=''>
              {brokerList.length
                ? t('screens.policiesScreen.newPanel.selectBroker', { defaultValue: 'Select broker (optional)' })
                : t('screens.policiesScreen.newPanel.noBrokersConfigured', { defaultValue: 'No brokers configured' })}
            </option>
            {brokerList.map(b => (
              <option key={b._id} value={b._id}>
                {b.name}{b.email ? ` — ${b.email}` : ''}
              </option>
            ))}
          </select>
        </FormField>

        {error && (
          <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <BtnSecondary onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>
            {t('screens.policiesScreen.newPanel.cancel')}
          </BtnSecondary>
          <BtnPrimary onClick={handleCreate} style={{ flex: 1, justifyContent: 'center' }} disabled={saving}>
            {saving ? t('screens.policiesScreen.newPanel.saving') : t('screens.policiesScreen.newPanel.createPolicy')}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
});

export default NewPolicyPanel;
